import { useState, useEffect, useRef } from "react";
import { WifiOff, Wifi, Loader2 } from "lucide-react";

type OfflineReason = "browser" | "backend" | null;

const CONFIRMATION_MS = 2000;

export function OfflineIndicator() {
  const [reason, setReason] = useState<OfflineReason>(() =>
    navigator.onLine ? null : "browser"
  );
  const [confirmed, setConfirmed] = useState<OfflineReason>(null);
  const [syncCount, setSyncCount] = useState(0);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearConfirmTimer = () => {
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    };

    const showConfirmed = (kind: Exclude<OfflineReason, null>) => {
      clearConfirmTimer();
      setConfirmed(kind);
      confirmTimerRef.current = setTimeout(() => {
        setConfirmed(null);
        confirmTimerRef.current = null;
      }, CONFIRMATION_MS);
    };

    const goOffline = () => {
      clearConfirmTimer();
      setConfirmed(null);
      setReason("browser");
    };
    const goOnline = () =>
      setReason((prev) => {
        if (prev === "browser") {
          showConfirmed("browser");
          return null;
        }
        return prev;
      });
    const goUnreachable = () => {
      clearConfirmTimer();
      setConfirmed(null);
      setReason("backend");
    };
    const goReachable = () =>
      setReason((prev) => {
        if (prev === "backend") {
          showConfirmed("backend");
          return null;
        }
        return prev;
      });
    const onSync = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setSyncCount(detail.synced);
      setTimeout(() => setSyncCount(0), 3000);
    };

    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    window.addEventListener("api-unreachable", goUnreachable);
    window.addEventListener("api-reachable", goReachable);
    window.addEventListener("sw-sync-complete", onSync);

    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      window.removeEventListener("api-unreachable", goUnreachable);
      window.removeEventListener("api-reachable", goReachable);
      window.removeEventListener("sw-sync-complete", onSync);
      clearConfirmTimer();
    };
  }, []);

  const isVisible = reason !== null;
  const showConfirmation = !isVisible && confirmed !== null;

  const message =
    reason === "browser"
      ? "You're offline. Check your connection."
      : "Can't reach the server. Retrying…";

  if (!isVisible && !showConfirmation && syncCount === 0) return null;

  return (
    <>
      {isVisible && (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          data-testid="offline-banner"
          className="fixed top-0 left-0 right-0 z-[10000] flex items-center justify-center gap-2 px-4 py-2 bg-amber-900 border-b border-amber-600 text-amber-100 text-xs sm:text-sm font-medium"
        >
          <WifiOff size={14} aria-hidden="true" className="shrink-0" />
          <span>{message}</span>
          {reason === "backend" && (
            <Loader2
              size={14}
              aria-hidden="true"
              data-testid="offline-banner-spinner"
              className="shrink-0 animate-spin"
            />
          )}
        </div>
      )}
      {showConfirmation && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="connected-banner"
          className="fixed top-0 left-0 right-0 z-[10000] flex items-center justify-center gap-2 px-4 py-2 bg-emerald-900 border-b border-emerald-500 text-emerald-100 text-xs sm:text-sm font-medium animate-in fade-in"
        >
          <Wifi size={14} aria-hidden="true" className="shrink-0" />
          <span>Connected</span>
        </div>
      )}
      {syncCount > 0 && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="sync-complete-banner"
          className="fixed bottom-4 left-4 z-[100] max-w-xs bg-emerald-900/90 border border-emerald-500/30 text-emerald-200 px-4 py-3 rounded-lg flex items-center gap-3 text-sm backdrop-blur-sm shadow-lg animate-in fade-in"
        >
          <span>
            {syncCount} change{syncCount !== 1 ? "s" : ""} synced!
          </span>
        </div>
      )}
    </>
  );
}
